import { FormEvent, useState } from 'react';
import type { ModernizationCostSplit } from '../../api';
import type { User } from '../../types';
import { useFeedback } from '../../hooks/useFeedback';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import ModalOverlay from '../modal/ModalOverlay';
import ModalPortal from '../modal/ModalPortal';
import '../modal/ModalBase.css';
import './AddModernizationModal.css';

const todayIso = () => {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

export type AddModernizationPayload = {
  description: string;
  performed_by: string;
  performed_at: string;
  cost: number;
  cost_split: ModernizationCostSplit;
  payer_user_id?: number;
  comment: string;
};

type Props = {
  members: User[];
  currentUserId?: number;
  title?: string;
  confirmLabel?: string;
  initial?: Partial<AddModernizationPayload> | null;
  onClose: () => void;
  onConfirm: (data: AddModernizationPayload) => void | Promise<void>;
};

const toDateInput = (value?: string | null) => {
  if (!value) return todayIso();
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  return todayIso();
};

const AddModernizationModal = ({
  members,
  currentUserId = 0,
  title = 'Добавить модернизацию',
  confirmLabel = 'Добавить',
  initial,
  onClose,
  onConfirm,
}: Props) => {
  const { showError, feedbackNode } = useFeedback();
  const { submitting, runSubmit } = useSubmitGuard();
  const [form, setForm] = useState({
    description: initial?.description || '',
    performed_by: initial?.performed_by || '',
    performed_at: toDateInput(initial?.performed_at),
    cost: initial?.cost != null ? String(initial.cost) : '',
    cost_split: (initial?.cost_split || 'shares') as ModernizationCostSplit,
    payer_user_id: initial?.payer_user_id || currentUserId,
    comment: initial?.comment || '',
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const cost = Number(String(form.cost).replace(',', '.'));
    if (!form.description.trim()) {
      showError('Укажите описание улучшения');
      return;
    }
    if (!form.performed_at) {
      showError('Укажите дату');
      return;
    }
    if (!form.performed_by.trim()) {
      showError('Укажите, кем сделано');
      return;
    }
    if (!Number.isFinite(cost) || cost < 0) {
      showError('Укажите стоимость в рублях');
      return;
    }
    if (form.cost_split === 'personal' && !form.payer_user_id) {
      showError('Укажите, кто оплатил улучшение');
      return;
    }
    void runSubmit(() =>
      onConfirm({
        description: form.description.trim(),
        performed_by: form.performed_by.trim(),
        performed_at: form.performed_at,
        cost,
        cost_split: form.cost_split,
        payer_user_id:
          form.cost_split === 'personal' ? form.payer_user_id : undefined,
        comment: form.comment.trim(),
      })
    );
  };

  return (
    <ModalPortal>
      <ModalOverlay onClose={onClose}>
        {feedbackNode}
        <form
          className="modal"
          onClick={(e) => e.stopPropagation()}
          onSubmit={handleSubmit}
        >
          <h3>{title}</h3>
          <div className="modal-body">
            <label>Описание улучшения</label>
            <input
              type="text"
              placeholder="Что сделано"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              required
            />
            <label>Когда</label>
            <input
              type="date"
              value={form.performed_at}
              onChange={(e) =>
                setForm({ ...form, performed_at: e.target.value })
              }
              max={todayIso()}
              required
            />
            <label>Кем сделано</label>
            <input
              type="text"
              placeholder="ФИО / организация"
              value={form.performed_by}
              onChange={(e) =>
                setForm({ ...form, performed_by: e.target.value })
              }
              required
            />
            <label>Стоимость, ₽</label>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0"
              value={form.cost}
              onChange={(e) => setForm({ ...form, cost: e.target.value })}
              required
            />
            <label>Комментарий</label>
            <textarea
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              placeholder="Любая информация по этой модернизации..."
              rows={5}
            />
            <label>Тип расхода</label>
            <div className="mod-split-radios">
              <label>
                <input
                  type="radio"
                  name="mod-cost-split"
                  checked={form.cost_split === 'shares'}
                  onChange={() => setForm({ ...form, cost_split: 'shares' })}
                />
                Долевой
              </label>
              <label>
                <input
                  type="radio"
                  name="mod-cost-split"
                  checked={form.cost_split === 'personal'}
                  onChange={() =>
                    setForm({
                      ...form,
                      cost_split: 'personal',
                      payer_user_id: form.payer_user_id || currentUserId || 0,
                    })
                  }
                />
                Личный
              </label>
            </div>
            {form.cost_split === 'personal' ? (
              <>
                <label>Кто оплатил</label>
                <select
                  value={form.payer_user_id || ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      payer_user_id: Number(e.target.value) || 0,
                    })
                  }
                  required
                >
                  <option value="">Выберите участника</option>
                  {members.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Отмена
            </button>
            <button type="submit" className="btn-save" disabled={submitting}>
              {submitting ? '…' : confirmLabel}
            </button>
          </div>
        </form>
      </ModalOverlay>
    </ModalPortal>
  );
};

export default AddModernizationModal;
